import type { CenterProps } from '@hover/blueprint';
import {
  Center,
  Icon,
  IconButton,
  calc,
  transition,
  useTheme,
} from '@hover/blueprint';
import { iconArrowLeft, iconArrowRight } from '@hover/icons';
import React from 'react';

import { withHover } from 'src/modules/homeownerApp/helpers';
import {
  useHomeownerAppContext,
  useHomeownerTranslate,
} from 'src/modules/homeownerApp/hooks';
import { buttonPressed } from 'src/shared/telemetry';

import { useCustomization } from '../useCustomization';
import { useCustomizeTargetTracking } from '../useCustomizeTargetTracking';
import { useUndoRedo } from '../useUndoRedo';

import { $arrowDistance, imagePadding } from './constants';

interface ArrowProps extends CenterProps {
  direction: 'undo' | 'redo';
}

const Arrow = ({ direction, sx, ...props }: ArrowProps) => {
  const t = useHomeownerTranslate();
  const { category } = useCustomization();

  const { canUndo, canRedo, undo, redo, next, previous } = useUndoRedo();

  const { trackingData: contextTrackingData } = useHomeownerAppContext();
  const customizeTargetTracking = useCustomizeTargetTracking();

  const isUndo = direction === 'undo';
  const isEnabled = direction === 'undo' ? canUndo : canRedo;

  const label = t(`customize.${direction}`);

  const undoEditedImageId = previous?.aiGeneratedImageId ?? undefined;
  const redoEditedImageId = next?.aiGeneratedImageId ?? undefined;

  const handleClick = () => {
    buttonPressed({
      ...contextTrackingData,
      ...customizeTargetTracking,
      button_text: label ?? '',
      edited_image_id: isUndo ? undoEditedImageId : redoEditedImageId,
      track_id: 'edit-history-navigation',
    });

    if (isUndo) {
      undo();
    } else {
      redo();
    }
  };

  const { space } = useTheme();

  return (
    <Center
      display={{ base: 'none', tablet: 'flex' }}
      opacity={isEnabled && category ? 1 : 0}
      pointerEvents={{ base: 'auto', tablet: 'none' }}
      position="absolute"
      sx={{
        ...sx,
        ...withHover({ display: 'flex' }),
        [$arrowDistance.variable]: {
          base: space[200],
          desktop: space[400],
          width500: space[600],
        },
      }}
      top="50%"
      transform={{
        base: `translateX(${isUndo ? '-50%' : '50%'}) translateY(-50%)`,
        tablet: `translateX(${isUndo ? '-100%' : '100%'}) translateY(-50%)`,
      }}
      {...{
        [isUndo ? 'left' : 'right']: {
          base: 0,
          tablet: calc.multiply(calc.add(imagePadding, $arrowDistance), -1),
        },
      }}
      {...transition.easing250('opacity')}
      {...props}
    >
      <IconButton
        colorScheme="dark"
        display={{ tablet: 'none' }}
        icon={isUndo ? iconArrowLeft : iconArrowRight}
        isDisabled={!isEnabled}
        label={label}
        onClick={handleClick}
        shape="circle"
        variant="glass"
      />
      <Icon
        aria-hidden
        color="neutral.0"
        display={{ base: 'none', tablet: 'flex' }}
        icon={isUndo ? iconArrowLeft : iconArrowRight}
        pointerEvents="none"
        size={{ base: 'medium', tablet: 'large' }}
        strokeWidth="2px"
      />
    </Center>
  );
};

export { Arrow };
